import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { consultationService } from '../../../lib/institutionService';
import { treatmentServices } from '../../../mocks/treatmentServices';

interface ConsultationRequestModalProps {
  institutionId: string;
  institutionName: string;
  services?: string[];
  onClose: () => void;
}

export default function ConsultationRequestModal({
  institutionId,
  institutionName,
  onClose,
}: ConsultationRequestModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setIsLoggedIn(false); return; }
        setIsLoggedIn(true);
        setCurrentUserId(user.id);
        const { data: profile } = await supabase
          .from('user_profiles').select('full_name, phone').eq('id', user.id).maybeSingle();
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          fullName: profile?.full_name || user.user_metadata?.full_name || '',
          phone: profile?.phone || user.user_metadata?.phone || '',
        }));
      } catch {
        setIsLoggedIn(false);
      }
    };
    init();
  }, [institutionId]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!formData.fullName.trim()) errs.fullName = t('consult_err_fullname');
    if (!formData.email.trim()) errs.email = t('consult_err_email');
    if (!formData.phone.trim()) errs.phone = t('consult_err_phone');
    if (!formData.subject) errs.subject = t('consult_err_subject');
    if (!formData.message.trim()) errs.message = t('consult_err_message');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('sending');
    setErrorMessage('');
    try {
      const messageWithSubject = `[${formData.subject}] ${formData.message}`;
      const { error } = await consultationService.create({
        institution_id: institutionId,
        user_id: currentUserId,
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: messageWithSubject,
      });
      if (error) {
        setErrorMessage(t('consult_err_generic'));
        setStatus('error');
        return;
      }
      setStatus('success');
    } catch {
      setErrorMessage(t('consult_err_unexpected'));
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  if (isLoggedIn === null) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 flex items-center justify-center">
          <span className="animate-spin w-6 h-6 border-2 border-rose-500 border-t-transparent rounded-full"></span>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
          <div className="relative bg-gradient-to-br from-rose-500 to-rose-400 p-8 text-center">
            <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
              <i className="ri-lock-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{t('consult_login_required')}</h3>
            <p className="text-sm text-white/80">{t('consult_login_desc')}</p>
          </div>
          <div className="p-6">
            <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg mb-5">
              <p className="text-xs text-rose-600 flex items-start">
                <i className="ri-information-line mr-1.5 mt-0.5 flex-shrink-0"></i>
                <span dangerouslySetInnerHTML={{ __html: t('consult_login_info', { name: institutionName }) }} />
              </p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => { onClose(); navigate('/kullanici-paneli?redirect=' + encodeURIComponent(window.location.pathname)); }}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-500 transition-all text-sm whitespace-nowrap cursor-pointer shadow-lg shadow-rose-200/50"
              >
                <i className="ri-login-box-line mr-2"></i>
                {t('consult_login_btn')}
              </button>
              <button
                onClick={() => { onClose(); navigate('/uye-ol?redirect=' + encodeURIComponent(window.location.pathname)); }}
                className="w-full px-6 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line mr-2"></i>
                {t('consult_register_btn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{t('consult_title')}</h3>
              <p className="text-sm text-gray-500 mt-1">{institutionName}</p>
            </div>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-3xl text-emerald-600"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{t('consult_success_title')}</h4>
              <p className="text-sm text-gray-600 mb-1">{t('consult_success_desc')}</p>
              <p className="text-sm text-gray-500">{t('consult_success_hint')}</p>
              <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                {t('consult_close')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg mb-2">
                <p className="text-xs text-rose-600 flex items-start">
                  <i className="ri-information-line mr-1.5 mt-0.5 flex-shrink-0"></i>
                  <span>{t('consult_info')}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('consult_fullname')} *</label>
                <input
                  type="text" name="fullName" value={formData.fullName} onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none text-sm transition-colors ${errors.fullName ? 'border-red-400' : 'border-gray-300 focus:border-rose-500'}`}
                  placeholder={t('consult_fullname_placeholder')}
                />
                {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('consult_email')} *</label>
                  <input
                    type="email" name="email" value={formData.email} onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg outline-none text-sm transition-colors ${errors.email ? 'border-red-400' : 'border-gray-300 focus:border-rose-500'}`}
                    placeholder="example@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('consult_phone')} *</label>
                  <input
                    type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg outline-none text-sm transition-colors ${errors.phone ? 'border-red-400' : 'border-gray-300 focus:border-rose-500'}`}
                    placeholder={t('consult_phone_placeholder')}
                  />
                  {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('consult_treatment')} *</label>
                <select
                  name="subject" value={formData.subject} onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none text-sm cursor-pointer transition-colors ${errors.subject ? 'border-red-400' : 'border-gray-300 focus:border-rose-500'}`}
                >
                  <option value="">{t('consult_treatment_placeholder')}</option>
                  {treatmentServices.map(t2 => (
                    <option key={t2.id} value={t2.title}>{t2.title}</option>
                  ))}
                  <option value="Other">{t('consult_treatment_other')}</option>
                </select>
                {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('consult_message')} *</label>
                <textarea
                  name="message" value={formData.message} onChange={handleChange}
                  maxLength={500} rows={4}
                  className={`w-full px-4 py-3 border rounded-lg outline-none text-sm resize-none transition-colors ${errors.message ? 'border-red-400' : 'border-gray-300 focus:border-rose-500'}`}
                  placeholder={t('consult_message_placeholder')}
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
                {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
              </div>

              {status === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    {errorMessage || t('consult_err_generic')}
                  </p>
                </div>
              )}

              <button
                type="submit" disabled={status === 'sending'}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-rose-500 to-rose-400 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-rose-500 transition-all text-sm disabled:opacity-50 whitespace-nowrap cursor-pointer shadow-lg shadow-rose-200/50"
              >
                {status === 'sending' ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    {t('consult_submitting')}
                  </span>
                ) : t('consult_submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
